import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  RefObject,
} from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_BASE } from '../config';

/* ------------------------------------------------------------------ */
/*  TYPES                                                              */
/* ------------------------------------------------------------------ */
interface EmailTemplate {
  _id: string;
  title: string;
  subject: string;
  design: any;
  htmlContent: string;
  createdBy: { name: string; email: string };
  createdAt: string;
  isActive: boolean;
}

interface EmailHistory {
  _id: string;
  templateId: { title: string; subject: string } | null;
  subject: string;
  recipients: string[];
  status: 'sent' | 'failed' | 'pending';
  sentAt: string;
  error?: string;
  messageId?: string;
}

type Design = {
  counters: Record<string, number>;
  body: {
    id: string;
    rows: any[];
    values: Record<string, any>;
    headers: any[];
    footers: any[];
  };
};

const blankDesign: Design = {
  counters: {},
  body: { id: 'body', rows: [], values: {}, headers: [], footers: [] },
};

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                          */
/* ------------------------------------------------------------------ */
const Mailer: React.FC = () => {
  const emailEditorRef: RefObject<EditorRef> = useRef(null);

  /* ------------- STATE ------------- */
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTpl, setSelectedTpl] = useState<EmailTemplate | null>(null);

  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');

  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const [activeEmails, setActiveEmails] = useState<string[]>([]);
  const [inactiveEmails, setInactiveEmails] = useState<string[]>([]);
  const [allEmails, setAllEmails] = useState<string[]>([]);

  const [history, setHistory] = useState<EmailHistory[]>([]);
  const [activeTab, setActiveTab] = useState<'compose' | 'templates' | 'history'>('compose');

  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);

  /* ------------- CONSTANTS ------------- */
  const emailHeader = `
    <div style="display:flex;align-items:center;padding-bottom:10px;border-bottom:1px solid #ddd;margin-bottom:20px">
      <img src="${API_BASE}/public/logo.png" alt="Growwin Logo" style="max-width:50px;height:auto;margin-right:10px" />
      <h2 style="margin:0;color:#523680;font-size:24px;line-height:50px">Growwin Capital</h2>
    </div>
  `;

  const tokenHdr = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  /* ----------------------------------------------------------------
     FETCH DATA
  ------------------------------------------------------------------*/
  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/email/templates`,
        tokenHdr
      );
      setTemplates(data);
    } catch {
      toast.error('Failed to fetch templates');
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/email/history`,
        tokenHdr
      );
      setHistory(data);
    } catch {
      toast.error('Failed to fetch history');
    }
  }, []);

  /* fetch recipient pools once */
  useEffect(() => {
    fetchTemplates();
    fetchHistory();

    axios.get(`${API_BASE}/api/email/active-investors`, tokenHdr).then(r => setActiveEmails(r.data));
    axios.get(`${API_BASE}/api/email/inactive-investors`, tokenHdr).then(r => setInactiveEmails(r.data));
    axios.get(`${API_BASE}/api/email/all-investors`, tokenHdr).then(r => setAllEmails(r.data));
  }, [fetchTemplates, fetchHistory]);

  /* ----------------------------------------------------------------
     HELPER
  ------------------------------------------------------------------*/
  const editor = () => emailEditorRef.current?.editor;

  const exportDesignAndHtml = (): Promise<{ design: any; html: string }> =>
    new Promise(resolve => editor()?.exportHtml(data => resolve(data)));

  /* ----------------------------------------------------------------
     RECIPIENT HANDLERS
  ------------------------------------------------------------------*/
  const addRecipient = (email: string) => {
    setRecipients(prev => (prev.includes(email) ? prev : [...prev, email]));
  };

  const handleRecipientInput = (val: string) => {
    setRecipientInput(val);

    if (val.length < 2) return setSuggestions([]);

    const pool = activeEmails
      .concat(inactiveEmails, allEmails)
      .filter((em, idx, arr) => arr.indexOf(em) === idx);   // ⬅️ no Set

    setSuggestions(
      pool
        .filter(
          em =>
            em.toLowerCase().includes(val.toLowerCase()) &&
            !recipients.includes(em)
        )
        .slice(0, 8)
    );
  };


  /* ----------------------------------------------------------------
     TEMPLATE ACTIONS
  ------------------------------------------------------------------*/
  const handleTemplateSelect = (tpl: EmailTemplate) => {
    setSelectedTpl(tpl);
    setTitle(tpl.title);
    setSubject(tpl.subject);
    editor()?.loadDesign(tpl.design);
    setActiveTab('compose');
  };

  const handleSaveTemplate = async () => {
    if (!title || !subject) return toast.error('Title & subject required');

    const { design, html } = await exportDesignAndHtml();
    const payload = { title, subject, design, htmlContent: html };

    try {
      if (selectedTpl) {
        await axios.put(
          `${API_BASE}/api/email/templates/${selectedTpl._id}`,
          payload,
          tokenHdr
        );
        toast.success('Template updated');
      } else {
        await axios.post(`${API_BASE}/api/email/templates`, payload, tokenHdr);
        toast.success('Template saved');
      }
      fetchTemplates();
    } catch {
      toast.error('Save failed');
    }
  };

  /* ----------------------------------------------------------------
     PREVIEW & SEND
  ------------------------------------------------------------------*/
  const handlePreview = async () => {
    const { html } = await exportDesignAndHtml();
    setPreviewHtml(emailHeader + html);
  };

  const handleSendEmail = async () => {
    if (!subject || recipients.length === 0)
      return toast.error('Subject + at least one recipient required');

    const { html } = await exportDesignAndHtml();
    setIsSending(true);

    try {
      await axios.post(
        `${API_BASE}/api/email/send`,
        {
          templateId: selectedTpl?._id || null,
          recipients,
          subject,
          htmlContent: emailHeader + html,
        },
        tokenHdr
      );
      toast.success('Emails queued');
      fetchHistory();
    } catch {
      toast.error('Send failed');
    } finally {
      setIsSending(false);
    }
  };

  /* ----------------------------------------------------------------
     JSX
  ------------------------------------------------------------------*/
  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 bg-gray-50/30 min-h-screen">
      <ToastContainer />

      <h1 className="text-2xl md:text-4xl font-black mb-6 md:mb-10 text-gray-900 uppercase tracking-tight">Email Management System</h1>

      {/* ---------------- Tabs ---------------- */}
      <div className="flex mb-6 border-b border-gray-100 overflow-x-auto min-w-full">
        <div className="flex min-w-max">
          {(['compose', 'templates', 'history'] as const).map(tab => (
            <button
              key={tab}
              className={`px-4 md:px-8 py-3 md:py-4 text-xs md:text-lg font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'border-b-4 border-blue-600 text-blue-600 bg-blue-50/50' : 'text-gray-400 hover:text-blue-600'
                }`}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'compose' && !selectedTpl) editor()?.loadDesign(blankDesign);
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* ================= COMPOSE ================ */}
      {activeTab === 'compose' && (
        <div className="bg-white p-6 rounded-xl shadow-lg">
          {/* Title / subject */}
          <label className="block text-sm mb-1">Template title</label>
          <input
            className="w-full border rounded p-2 mb-4"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />

          <label className="block text-sm mb-1">Subject</label>
          <input
            className="w-full border rounded p-2 mb-4"
            value={subject}
            onChange={e => setSubject(e.target.value)}
          />

          {/* Recipients input */}
          <label className="block text-sm mb-1">Recipients</label>
          <div className="relative">
            <div className="flex gap-2">
              <input
                className="flex-1 border rounded p-2"
                placeholder="type email or search…"
                value={recipientInput}
                onChange={e => handleRecipientInput(e.target.value)}
              />
              {/* <button onClick={() => addRecipient(recipientInput.trim())} className="bg-blue-600 text-white px-4 rounded">
                Add
              </button> */}
            </div>

            {/* Suggestion list */}
            {suggestions.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full border rounded bg-white max-h-40 overflow-auto">
                {suggestions.map(em => (
                  <li
                    key={em}
                    className="px-3 py-1 hover:bg-blue-100 cursor-pointer"
                    onClick={() => {
                      addRecipient(em);
                      setRecipientInput('');
                      setSuggestions([]);
                    }}
                  >
                    {em}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Bulk buttons */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setRecipients(Array.from(new Set([...recipients, ...activeEmails])))}
              className="text-xs bg-blue-200 hover:bg-blue-300 px-2 py-1 rounded"
            >
              + Active ({activeEmails.length})
            </button>
            <button
              onClick={() => setRecipients(Array.from(new Set([...recipients, ...inactiveEmails])))}
              className="text-xs bg-blue-200 hover:bg-blue-300 px-2 py-1 rounded"
            >
              + Inactive ({inactiveEmails.length})
            </button>
            <button
              onClick={() => setRecipients(Array.from(new Set([...recipients, ...allEmails])))}
              className="text-xs bg-blue-200 hover:bg-blue-300 px-2 py-1 rounded"
            >
              + Everyone ({allEmails.length})
            </button>

            <button
              onClick={() => setRecipients([])}
              className="text-xs bg-red-200 hover:bg-red-300 px-2 py-1 rounded ml-auto"
            >
              Clear All
            </button>
          </div>

          {/* Chips */}
          {recipients.length > 0 && (
            <div className="border rounded p-3 mt-3 max-h-32 overflow-auto">
              {recipients.map(em => (
                <span
                  key={em}
                  className="inline-flex items-center bg-blue-100 text-blue-800 text-xs mr-2 mb-2 px-2 py-1 rounded-full"
                >
                  {em}
                  <button className="ml-1 text-red-600" onClick={() => setRecipients(recipients.filter(r => r !== em))}>
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Editor */}
          <div className="h-[560px] border rounded overflow-hidden mt-4">
            <EmailEditor
              ref={emailEditorRef}
              minHeight="550px"
              options={
                {
                  imageUploadURL: `${API_BASE}/api/emailUpload/upload`,
                  imageUploadHeaders: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                } as any
              }
              onReady={() => {
                if (selectedTpl) editor()?.loadDesign(selectedTpl.design);
              }}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-6">
            <button onClick={handleSaveTemplate} className="bg-green-600 text-white px-4 py-2 rounded">
              Save
            </button>
            <button
              onClick={async () => {
                await handlePreview();
                setShowPreview(true);
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded"
            >
              Preview
            </button>
            <button
              onClick={handleSendEmail}
              className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
              disabled={isSending}
            >
              {isSending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* ================= TEMPLATES ================ */}
      {activeTab === 'templates' && (
        <div className="bg-white p-4 md:p-8 rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-black mb-6 text-gray-800 uppercase tracking-tight">Saved templates</h2>
          {templates.length === 0 ? (
            <p className="text-center py-10 text-gray-400 italic">No templates yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(tpl => (
                <div key={tpl._id} className="p-4 bg-white border border-gray-100 rounded-2xl hover:shadow-lg transition-all flex flex-col justify-between group">
                  <div className="mb-4">
                    <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight truncate">{tpl.title}</h3>
                    <p className="text-[10px] text-gray-400 font-bold mt-1 truncate uppercase">{tpl.subject}</p>
                  </div>
                  <div className="flex gap-2 pt-3 border-t border-gray-50">
                    <button
                      className="flex-1 py-2 text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-50"
                      onClick={() => handleTemplateSelect(tpl)}
                    >
                      Use
                    </button>
                    <button
                      className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-600 bg-gray-50 rounded-lg hover:bg-red-50 transition-all"
                      onClick={async () => {
                        if (window.confirm('Delete template?')) {
                          await axios.delete(
                            `${API_BASE}/api/email/templates/${tpl._id}`,
                            tokenHdr
                          );
                          toast.success('Deleted');
                          fetchTemplates();
                        }
                      }}
                    >
                      Del
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================= HISTORY ================ */}
      {activeTab === 'history' && (
        <div className="bg-white p-4 md:p-8 rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-black mb-6 text-gray-800 uppercase tracking-tight">Email history</h2>
          {history.length === 0 ? (
            <p className="text-center py-10 text-gray-400 italic">No mails yet.</p>
          ) : (
            <div className="space-y-4">
              {history.map(h => (
                <div key={h._id} className="p-4 md:p-6 border border-gray-100 rounded-2xl bg-white hover:shadow-md transition-all relative overflow-hidden group">
                  <div className="absolute top-0 left-0 w-1 bg-blue-500 h-full opacity-0 group-hover:opacity-100 transition-opacity"></div>

                  <div className="flex justify-between items-start mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-black text-gray-900 truncate uppercase tracking-tight">{h.subject}</h3>
                      <p className="text-[10px] text-gray-400 font-bold mt-1 uppercase">{new Date(h.sentAt).toLocaleString('en-GB')}</p>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter ${h.status === 'sent' ? 'bg-green-50 text-green-600' : h.status === 'failed' ? 'bg-red-50 text-red-600' : 'bg-yellow-50 text-yellow-600'
                      }`}>
                      {h.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-2 bg-gray-50/50 p-3 rounded-xl border border-gray-50">
                    <div>
                      <span className="block text-[9px] text-gray-400 font-bold uppercase mb-1">Recipients ({h.recipients.length})</span>
                      <p className="text-[10px] text-gray-700 font-medium truncate italic">{h.recipients.join(', ')}</p>
                    </div>
                    {h.error && (
                      <div className="mt-1 pt-1 border-t border-red-50">
                        <span className="block text-[9px] text-red-400 font-bold uppercase mb-0.5">Error Log</span>
                        <p className="text-[9px] text-red-600 font-bold leading-relaxed">{h.error}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ----------- Preview modal ----------- */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white p-6 rounded shadow-xl max-w-[794px] w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">A4 preview</h2>
              <button onClick={() => setShowPreview(false)} className="text-red-600 text-xl">
                &times;
              </button>
            </div>
            <div
              className="border"
              style={{ width: '794px', minHeight: '1123px' }} /* A4 @96 dpi */
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>
      )}

      {/* ----------- Send loader ----------- */}
      {isSending && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50">
          <div className="bg-white p-6 rounded-full shadow-lg animate-pulse">Sending…</div>
        </div>
      )}
    </div>
  );
};

export default Mailer;
